import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {UserService} from '@services/administration/user/user.service';
import {NotificationService} from '@services/notification/notification.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {ConfirmationDialogService} from '@services/confirmation-dialog.service';
import {User} from '@models/user/user';
import {UserEditComponent} from '@components/administration/user/modal/user-edit.component';
import {USER_ROLE} from '@constants/user-role.constant';
import {Subject, throwError} from 'rxjs';
import {catchError, switchMap, takeUntil} from 'rxjs/operators';
import {cloneDeep} from 'lodash';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';
import {ModalService} from '@services/modal/modal.service';
import { Role } from '@models/user/role';

@Component({
    selector: 'scitis-admin-user-action',
    templateUrl: './user-action.component.html',
    styleUrls: ['./user-action.component.scss'],
    standalone: false
})
export class UserActionComponent implements OnInit, OnDestroy {

  @Input()
  user: User = new User();

  @Input()
  loggedUser: User = new User();

  @Input()
  restrictUserInteraction: boolean = false;

  @Input()
  loggedUserIsAdminOrHigher: boolean = false;

  @Output()
  userDeleteEvent = new EventEmitter<User>();

  @Output()
  userUpdatedEvent = new EventEmitter<User>();

  public banClass: string = 'fa-solid fa-ban';
  public isDistributorRole: boolean = false;
  public changedUserLowerThanAdmin: boolean = false;
  private deleteUserMessageTitle = '';
  private deleteUserMesssageBody = '';
  private deleteUserMessageOK = '';
  private resetPasswordMessageTitle = '';
  private resetPasswordMessageBodyPart1 = '';
  private readonly resetPasswordMessageBodyPart2 = '';
  private resetPasswordMessageOK = '';
  private banUserMessageTitle = '';
  private banUserMessageBodyPart1 = '';
  private readonly areYouSureMessage = '';
  private banUserMessageOK = '';
  private unbanUserMessageTitle = '';
  private unbanUserMessageBody = '';
  private unbanUserMessageOK = '';
  private COMPONENT_NAME: string = 'UserActionComponent';
  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
      private modalService: ModalService,
      private translate: TranslateService,
      private userService: UserService,
      private notification: NotificationService,
      private loadingIndicator: LoadingIndicatorService,
      private confirmationDialogService: ConfirmationDialogService) {
    this.deleteUserMessageTitle = this.translate.instant('Delete user');
    this.deleteUserMesssageBody = this.translate.instant('Do you really want to permanently delete user %s ?');
    this.deleteUserMessageOK = this.translate.instant('Delete');
    this.resetPasswordMessageTitle = this.translate.instant('Reset password');
    this.resetPasswordMessageBodyPart1 = this.translate.instant('Do you really want to reset the password for user %s ?');
    this.resetPasswordMessageBodyPart2 = this.translate.instant('The user will receive an e-mail with his new password.');
    this.resetPasswordMessageOK = this.translate.instant('Reset password');
    this.banUserMessageTitle = this.translate.instant('Ban user');
    this.banUserMessageBodyPart1 = this.translate.instant('This will ban %s from the portal.');
    this.areYouSureMessage = this.translate.instant('Are you sure?');
    this.banUserMessageOK = this.translate.instant('Ban user');
    this.unbanUserMessageTitle = this.translate.instant('Unban user');
    this.unbanUserMessageBody = this.translate.instant('This will grant user %s access to portal.');
    this.unbanUserMessageOK = this.translate.instant('Unban user');
  }

  ngOnInit(): void {
    if (this.user.banned) {
      this.banClass = 'fa-solid fa-circle-check';
    } else {
      this.banClass = 'fa-solid fa-ban';
    }
    this.setDistributorRole();
    this.setIsUserLowerThanAdmin();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  get isNotAllowsToEditUser() {
    const loggedRole = new Role(this.loggedUser);
        const selectedRole = new Role(this.user);
        const loggedUserIsAnalyticsOrLower = loggedRole.isUserRole || loggedRole.isViewerRole || loggedRole.isAnalyticsRole;
        const isHigherUserRole = loggedRole.isDistributorRole && !this.changedUserLowerThanAdmin 
                                 || loggedRole.isAdminRole && !(this.changedUserLowerThanAdmin || selectedRole.isAdminRole);
        return loggedUserIsAnalyticsOrLower || isHigherUserRole;
      }
      
  get isNotAllowedToBanUser() {
    const isLoggedInUser = this.user.firebaseUid === this.loggedUser.firebaseUid;
    return this.isNotAllowsToEditUser || isLoggedInUser;
  }

  public setDistributorRole(): void {
    if (this.loggedUser.role) {
      this.isDistributorRole = this.loggedUser.role === USER_ROLE.DISTRIBUTOR;
    }
  }

  public setIsUserLowerThanAdmin(): void {
    if (this.user) {
      const {role} = this.user;
      this.changedUserLowerThanAdmin = role !== USER_ROLE.ADMIN && role !== USER_ROLE.TECH_ADMIN && role !== USER_ROLE.SUPERADMIN;
    }
  }

  public onDeleteUser(): void {
    console.log(`${this.COMPONENT_NAME}. removing user: ${this.user.displayName.toBold()}`);
    const deleteUserMessageBody = this.deleteUserMesssageBody.replace('%s', this.user.displayName.toBold());
    this.confirmationDialogService.handleConfirmationDialog(this.deleteUserMessageTitle, deleteUserMessageBody, '',
      this.deleteUserMessageOK)
      .pipe(
        takeUntil(this.destroy$),
        switchMap((userConfirmed: boolean) => {
          if (userConfirmed) {
            this.loadingIndicator.show(this.translate.instant('Deleting user'));
            return this.userService.delete(this.user.firebaseUid).pipe(catchError(error => throwError({confirmError: error})));
          } else {
            return throwError({confirmError: userConfirmed});
          }
        })
      ).subscribe(() => {
        this.userDeleteEvent.emit(this.user);
        this.loadingIndicator.hide();
        this.notification.success(this.translate.instant('User') + ' ' + this.user.displayName + ' ' +
          this.translate.instant('deleted') + '.', this.COMPONENT_NAME, false);
        }, (_error) => {
        if (_error?.confirmError) {
          this.loadingIndicator.hide();
          this.notification.error(
            this.translate.instant('User could not be deleted') + ': ' +
            this.user.displayName, _error, this.COMPONENT_NAME, true);
        } else {
          this.loadingIndicator.hide();
        }
    });
  }

  public onEditUser(): void {
    const userBackup = cloneDeep(this.user);
    const modalRef = this.modalService.open(UserEditComponent, {size: 'lg'});
    modalRef.componentInstance.user = new User(this.user);
    modalRef.componentInstance.loggedUser = new User(this.loggedUser);

    modalRef.result.then((result) => {
      console.log(`Closed with: ${result as User}`);
      this.userUpdatedEvent.emit(result);
    }, (reason) => {
      if (reason === ModalDialogDismissOption.CANCEL || reason === ModalDialogDismissOption.CLOSE || reason === ModalDialogDismissOption.BACKDROP) {
        this.user = userBackup;
      }
      console.log(`Closed with reason: ${reason}`);
    });
  }

  public onPasswordReset(): void {
    console.log(`${this.COMPONENT_NAME}. resetting password for user: ${this.user.displayName.toBold()}`);
    const resetPasswordMessageBody = this.resetPasswordMessageBodyPart1.replace('%s', this.user.displayName.toBold())
        + '</br>' + this.resetPasswordMessageBodyPart2;
    this.confirmationDialogService.handleConfirmationDialog(this.resetPasswordMessageTitle, resetPasswordMessageBody, '',
      this.resetPasswordMessageOK)
      .pipe(
        takeUntil(this.destroy$),
        switchMap((userConfirmed: boolean) => {
          if (userConfirmed) {
            return this.userService.resetPassword(this.user).pipe(catchError(error => throwError({confirmError: error})));
          } else {
            return throwError({confirmError: userConfirmed});
          }
        })
      ).subscribe((success) => {
      this.notification.success(this.translate.instant('User saved') + '. ' + this.translate.instant('Password reset for ') +
        success.displayName + '.', this.COMPONENT_NAME, false);
      }, (_error) => {
      if (_error?.confirmError) {
        this.loadingIndicator.hide();
        this.notification.error(
          this.translate.instant('User password could not be reset') + ': ' +
          this.user.displayName, _error, this.COMPONENT_NAME, true);
      } else {
        this.loadingIndicator.hide();
      }
    });
  }

  public onBanUser(): void {
    if (this.user.banned) {
      this.unbanUser();
    } else {
      this.banUser();
    }
  }

  private banUser(): void {
    console.log(`${this.COMPONENT_NAME}. ban user: ${this.user.displayName.toBold()}`);
    const banUserMessageBody = this.banUserMessageBodyPart1.replace('%s', this.user.displayName.toBold())
        + '</br>' + this.areYouSureMessage;
    this.confirmationDialogService.handleConfirmationDialog(this.banUserMessageTitle, banUserMessageBody, '',
      this.banUserMessageOK)
      .pipe(
        takeUntil(this.destroy$),
        switchMap((userConfirmed: boolean) => {
          if (userConfirmed) {
            this.loadingIndicator.show(this.translate.instant('Ban user'));
            this.user.banned = true;
            return this.userService.banUser(this.user).pipe(catchError(error => throwError({confirmError: error})));
          } else {
            return throwError({confirmError: userConfirmed});
          }
        })
      ).subscribe(() => {
      this.notification.success(this.translate.instant('User banned'), this.COMPONENT_NAME, false);
      this.userUpdatedEvent.emit(this.user);
      this.loadingIndicator.hide();
      }, (_error) => {
      if (_error?.confirmError) {
        this.user.banned = false;
        this.notification.error(
          this.translate.instant('Banning user failed') + ': ' +
          this.user.displayName, _error, this.COMPONENT_NAME, true);
        this.loadingIndicator.hide();
      } else {
        this.loadingIndicator.hide();
      }
    });
  }

  private unbanUser(): void {
    console.log(`${this.COMPONENT_NAME}. unban user: ${this.user.displayName.toBold()}`);
    const unbanUserMessageBody = this.unbanUserMessageBody.replace('%s', this.user.displayName.toBold())
        + '</br>' + this.areYouSureMessage;
    this.confirmationDialogService.handleConfirmationDialog(this.unbanUserMessageTitle, unbanUserMessageBody, '',
      this.unbanUserMessageOK)
      .pipe(
        takeUntil(this.destroy$),
        switchMap((userConfirmed: boolean) => {
          if (userConfirmed) {
            this.loadingIndicator.show(this.translate.instant('Unban user'));
            this.user.banned = false;
            return this.userService.banUser(this.user).pipe(catchError(error => throwError({confirmError: error})));
          } else {
            return throwError({confirmError: userConfirmed});
          }
        })
      )
      .subscribe(() => {
        this.notification.success(this.translate.instant('User unbanned'), this.COMPONENT_NAME, false);
        this.userUpdatedEvent.emit(this.user);
        this.loadingIndicator.hide();

      }, (_error) => {
        if (_error?.confirmError) {
          this.user.banned = true;
          this.notification.error(
            this.translate.instant('Banning user failed') + ': ' +
            this.user.displayName, _error, this.COMPONENT_NAME, true);
          this.loadingIndicator.hide();
        } else {
          this.loadingIndicator.hide();
        }
      });
  }

}

